import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { bankApi, invoiceApi, billApi, reportApi, clientApi, documentApi } from '@/services/api'
import { useClientStore } from '@/stores/clientStore'
import type { Invoice, Bill, BankAccount, ProfitLoss, CashSummary, Document } from '@/types'
import { formatCurrency } from '@/utils/currency'
import {
  Building2, ArrowRight, Send, Plus, CheckCircle, FolderOpen, FileText, Image, File,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Simple CSS bar chart matching Xero's style */
function BarChart({ bars, maxValue }: { bars: { label: string; values: number[]; colors: string[] }[]; maxValue: number }) {
  const height = 160
  return (
    <div className="flex items-end gap-1.5 justify-between" style={{ height }}>
      {bars.map((bar, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
          <div className="flex items-end gap-px w-full justify-center" style={{ height }}>
            {bar.values.map((v, vi) => (
              <div
                key={vi}
                className="rounded-t-sm flex-1 max-w-[20px]"
                style={{
                  height: maxValue > 0 ? `${Math.max((v / maxValue) * height, v > 0 ? 3 : 0)}px` : '0px',
                  backgroundColor: bar.colors[vi] || '#0078C8',
                }}
              />
            ))}
          </div>
          <span className="text-[10px] text-[rgba(0,10,30,0.75)] text-center leading-tight mt-1">{bar.label}</span>
        </div>
      ))}
    </div>
  )
}

function ageBuckets(items: { due_date: string; total: string; status: string }[], overdueOnly?: boolean) {
  const now = new Date()
  const buckets: { label: string; amount: number }[] = [
    { label: 'Older', amount: 0 },
    { label: 'This\nweek', amount: 0 },
    { label: `${weekRange(-1)}`, amount: 0 },
    { label: `${weekRange(-2)}`, amount: 0 },
    { label: `From\n${weekRange(-3)}`, amount: 0 },
  ]
  for (const item of items) {
    if (overdueOnly && item.status !== 'overdue' && item.status !== 'sent' && item.status !== 'approved') continue
    const due = new Date(item.due_date)
    const diff = Math.floor((now.getTime() - due.getTime()) / 86400000)
    const amt = parseFloat(item.total) || 0
    if (diff > 21) buckets[0].amount += amt
    else if (diff > 14) buckets[4].amount += amt
    else if (diff > 7) buckets[3].amount += amt
    else if (diff > 0) buckets[2].amount += amt
    else buckets[1].amount += amt
  }
  return buckets
}

function weekRange(weeksAgo: number) {
  const now = new Date()
  const start = new Date(now.getTime() + weeksAgo * 7 * 86400000)
  return `${start.getDate()} ${start.toLocaleString('en', { month: 'short' })}`
}

function sumByStatus(items: { status: string; total: string; amount_due?: string }[], statuses: string[], field: 'total' | 'amount_due' = 'total') {
  return items
    .filter((i) => statuses.includes(i.status))
    .reduce((s, i) => s + (parseFloat(field === 'amount_due' && i.amount_due ? i.amount_due : i.total) || 0), 0)
}

function countByStatus(items: { status: string }[], statuses: string[]) {
  return items.filter((i) => statuses.includes(i.status)).length
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ClientOverview() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)

  const [loading, setLoading] = useState(true)
  const [currency, setCurrency] = useState('USD')
  const [isPremium, setIsPremium] = useState(false)

  // Data
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [unreconciledCount, setUnreconciledCount] = useState(0)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [bills, setBills] = useState<Bill[]>([])
  const [profitLoss, setProfitLoss] = useState<ProfitLoss | null>(null)
  const [cashSummary, setCashSummary] = useState<CashSummary | null>(null)
  const [inboxDocs, setInboxDocs] = useState<Document[]>([])
  const [filedCount, setFiledCount] = useState(0)

  useEffect(() => {
    if (!clientId) return

    // Always fetch client + bank + reports (starter features)
    const starterPromises = [
      clientApi.get(clientId),
      bankApi.list(clientId),
      reportApi.profitLoss(clientId),
      reportApi.cashSummary(clientId),
    ]

    Promise.all(starterPromises)
      .then(([clientRes, bankRes, plRes, cashRes]) => {
        const client = clientRes.data.client
        setCurrentClient(client)
        setCurrency(client?.currency || 'USD')
        const premium = client?.plan_tier === 'premium'
        setIsPremium(premium)
        setBankAccounts(bankRes.data.bank_accounts || [])
        setProfitLoss(plRes.data)
        setCashSummary(cashRes.data)

        // Fetch document inbox
        documentApi.list(clientId).then((res) => {
          const docs: Document[] = res.data.documents || []
          setInboxDocs(docs.filter((d) => d.doc_status === 'inbox'))
          setFiledCount(docs.filter((d) => d.doc_status === 'filed').length)
        }).catch(() => {})

        // Fetch reconciliation count for each bank account
        const bankIds: string[] = (bankRes.data.bank_accounts || []).map((b: BankAccount) => b.id)
        if (bankIds.length > 0) {
          Promise.all(bankIds.map((id) => bankApi.reconciliationSummary(id).catch(() => null)))
            .then((results) => {
              const total = results.reduce((s, r) => s + (r?.data?.unreconciled || 0), 0)
              setUnreconciledCount(total)
            })
        }

        // Fetch invoices + bills only if premium
        if (premium) {
          Promise.all([invoiceApi.list(clientId), billApi.list(clientId)])
            .then(([invRes, billRes]) => {
              setInvoices(invRes.data.invoices || [])
              setBills(billRes.data.bills || [])
            })
            .catch(() => {})
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [clientId, setCurrentClient])

  // --- Derived data ---
  const fc = (v: number | string) => formatCurrency(v, currency)

  // Invoice stats
  const invAwaiting = sumByStatus(invoices, ['sent', 'overdue'], 'amount_due')
  const invOverdue = sumByStatus(invoices, ['overdue'], 'amount_due')
  const invOverdueCount = countByStatus(invoices, ['overdue'])
  const invAwaitingCount = countByStatus(invoices, ['sent', 'overdue'])
  const invDraftCount = countByStatus(invoices, ['draft'])
  const invDraftTotal = sumByStatus(invoices, ['draft'])
  const invBuckets = ageBuckets(invoices.filter((i) => ['sent', 'overdue'].includes(i.status)))
  const invMax = Math.max(...invBuckets.map((b) => b.amount), 1)

  // Bill stats
  const billAwaiting = sumByStatus(bills, ['approved', 'overdue'], 'amount_due')
  const billOverdue = sumByStatus(bills, ['overdue'], 'amount_due')
  const billOverdueCount = countByStatus(bills, ['overdue'])
  const billAwaitingCount = countByStatus(bills, ['approved', 'overdue'])
  const billDraftCount = countByStatus(bills, ['draft'])
  const billDraftTotal = sumByStatus(bills, ['draft'])
  const billBuckets = ageBuckets(bills.filter((b) => ['approved', 'overdue'].includes(b.status)))
  const billMax = Math.max(...billBuckets.map((b) => b.amount), 1)

  // Recent paid invoices (last 10)
  const recentPayments = invoices
    .filter((i) => i.status === 'paid' && i.amount_paid)
    .sort((a, b) => b.issue_date.localeCompare(a.issue_date))
    .slice(0, 10)

  // P&L
  const plNet = parseFloat(profitLoss?.net_profit || '0')
  const plRevenue = profitLoss?.revenue?.total ? parseFloat(profitLoss.revenue.total) : 0
  const plExpenses = profitLoss?.expenses?.total ? parseFloat(profitLoss.expenses.total) : 0
  const plMax = Math.max(plRevenue, plExpenses, 1)

  // Cash
  const cashIn = parseFloat(cashSummary?.total_received || '0')
  const cashOut = parseFloat(cashSummary?.total_spent || '0')
  const cashDiff = cashIn - cashOut

  // Task counts
  const overdueInvoicesCount = invOverdueCount
  const overdueBillsCount = billOverdueCount
  const awaitingApprovalCount = countByStatus(invoices, ['draft'])

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-64 w-full rounded-[12px]" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 w-full rounded-[12px]" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Page title */}
      <h1 className="text-[21px] font-bold text-[#000A1E] leading-[32px]">Business Overview</h1>

      {/* Row 1: Bank Account | Invoices owed | Bills to pay */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* ---- Bank Account Card ---- */}
        <Card size="dashboard">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[17px] font-bold text-[#000A1E]">
                {bankAccounts.length > 0 ? bankAccounts[0].name : 'Bank Account'}
              </h3>
            </div>
            {bankAccounts.length > 0 ? (
              <>
                <p className="text-[11px] text-[rgba(0,10,30,0.75)] mb-1">
                  {bankAccounts[0].bank_name} &middot; {bankAccounts[0].account_number}
                </p>
                <div className="flex gap-6 mt-3 mb-4">
                  <div>
                    <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">
                      {fc(bankAccounts[0].opening_balance)}
                    </p>
                    <p className="text-[11px] text-[rgba(0,10,30,0.75)]">Statement balance</p>
                  </div>
                  <div>
                    <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">
                      {fc(bankAccounts[0].opening_balance)}
                    </p>
                    <p className="text-[11px] text-[rgba(0,10,30,0.75)]">Balance in LedgerPro</p>
                  </div>
                </div>
                {bankAccounts.length > 1 && (
                  <p className="text-[12px] text-[rgba(0,10,30,0.75)] mb-3">
                    + {bankAccounts.length - 1} more account{bankAccounts.length > 2 ? 's' : ''}
                  </p>
                )}
                <Link
                  to={`/clients/${clientId}/banking`}
                  className="inline-flex items-center gap-1.5 text-[13px] text-[#0078C8] font-bold hover:underline"
                >
                  View bank accounts <ArrowRight className="w-3 h-3" />
                </Link>
              </>
            ) : (
              <div className="py-4">
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-4">No bank accounts connected yet.</p>
                <Button variant="pill" onClick={() => navigate(`/clients/${clientId}/banking`)}>
                  <Building2 className="w-4 h-4" /> Connect bank account
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---- Invoices Owed Card ---- */}
        <Card size="dashboard">
          <CardContent className="p-5">
            <h3 className="text-[17px] font-bold text-[#000A1E] mb-3">Invoices owed to you</h3>
            {isPremium ? (
              <>
                <div className="flex gap-6 mb-4">
                  <div>
                    <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">{fc(invAwaiting)}</p>
                    <p className="text-[11px] text-[rgba(0,10,30,0.75)]">{invAwaitingCount} awaiting payment</p>
                  </div>
                  <div>
                    <p className="text-[21px] font-bold text-[#CC0000] tabular-nums">{fc(invOverdue)}</p>
                    <p className="text-[11px] text-[rgba(0,10,30,0.75)]">{invOverdueCount} of {invAwaitingCount} overdue</p>
                  </div>
                </div>
                <BarChart
                  bars={invBuckets.map((b) => ({
                    label: b.label,
                    values: [b.amount],
                    colors: ['#0078C8'],
                  }))}
                  maxValue={invMax}
                />
                <div className="mt-3 space-y-1 text-[13px]">
                  <div className="flex justify-between">
                    <span className="text-[rgba(0,10,30,0.75)]">{invDraftCount} drafts</span>
                    <span className="text-[#000A1E] tabular-nums">{fc(invDraftTotal)}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <Button variant="pill" onClick={() => navigate(`/clients/${clientId}/invoices`)}>
                    <Send className="w-3.5 h-3.5" /> Send invoices
                  </Button>
                </div>
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-3">Upgrade to Premium for invoicing</p>
                <Button variant="pill" onClick={() => navigate(`/clients/${clientId}/settings`)}>Upgrade</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---- Bills to Pay Card ---- */}
        <Card size="dashboard">
          <CardContent className="p-5">
            <h3 className="text-[17px] font-bold text-[#000A1E] mb-3">Bills to pay</h3>
            {isPremium ? (
              <>
                <div className="flex gap-6 mb-4">
                  <div>
                    <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">{fc(billAwaiting)}</p>
                    <p className="text-[11px] text-[rgba(0,10,30,0.75)]">{billAwaitingCount} awaiting payment</p>
                  </div>
                  <div>
                    <p className="text-[21px] font-bold text-[#CC0000] tabular-nums">{fc(billOverdue)}</p>
                    <p className="text-[11px] text-[rgba(0,10,30,0.75)]">{billOverdueCount} of {billAwaitingCount} overdue</p>
                  </div>
                </div>
                <BarChart
                  bars={billBuckets.map((b) => ({
                    label: b.label,
                    values: [b.amount],
                    colors: ['#002A46'],
                  }))}
                  maxValue={billMax}
                />
                <div className="mt-3 space-y-1 text-[13px]">
                  <div className="flex justify-between">
                    <span className="text-[rgba(0,10,30,0.75)]">{billDraftCount} drafts</span>
                    <span className="text-[#000A1E] tabular-nums">{fc(billDraftTotal)}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <Button variant="pill" onClick={() => navigate(`/clients/${clientId}/bills`)}>
                    <Plus className="w-3.5 h-3.5" /> Add bills
                  </Button>
                </div>
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-3">Upgrade to Premium for bill tracking</p>
                <Button variant="pill" onClick={() => navigate(`/clients/${clientId}/settings`)}>Upgrade</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Tasks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card size="dashboard">
          <CardContent className="p-5">
            <h3 className="text-[17px] font-bold text-[#000A1E] mb-4">Tasks</h3>
            <div className="space-y-0">
              {/* Items to reconcile */}
              <Link
                to={`/clients/${clientId}/banking`}
                className="flex items-center py-3 border-b border-[#E8E9EB] hover:bg-[#F0F5FF] -mx-5 px-5 transition-colors"
              >
                <span className="text-[17px] font-bold text-[#000A1E] w-10">{unreconciledCount}</span>
                <div className="flex-1">
                  <p className="text-[13px] text-[#000A1E]">Items to reconcile</p>
                  {bankAccounts.length > 0 && (
                    <p className="text-[11px] text-[rgba(0,10,30,0.75)]">
                      {bankAccounts[0].bank_name} &middot; {bankAccounts[0].account_number}
                    </p>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-[#A6A9B0]" />
              </Link>

              {isPremium && (
                <>
                  {overdueInvoicesCount > 0 && (
                    <Link
                      to={`/clients/${clientId}/invoices`}
                      className="flex items-center py-3 border-b border-[#E8E9EB] hover:bg-[#F0F5FF] -mx-5 px-5 transition-colors"
                    >
                      <span className="text-[17px] font-bold text-[#000A1E] w-10">{overdueInvoicesCount}</span>
                      <span className="flex-1 text-[13px] text-[#000A1E]">Overdue invoices</span>
                      <ArrowRight className="w-4 h-4 text-[#A6A9B0]" />
                    </Link>
                  )}

                  {awaitingApprovalCount > 0 && (
                    <Link
                      to={`/clients/${clientId}/invoices`}
                      className="flex items-center py-3 border-b border-[#E8E9EB] hover:bg-[#F0F5FF] -mx-5 px-5 transition-colors"
                    >
                      <span className="text-[17px] font-bold text-[#000A1E] w-10">{awaitingApprovalCount}</span>
                      <span className="flex-1 text-[13px] text-[#000A1E]">Draft invoices</span>
                      <ArrowRight className="w-4 h-4 text-[#A6A9B0]" />
                    </Link>
                  )}

                  {overdueBillsCount > 0 && (
                    <Link
                      to={`/clients/${clientId}/bills`}
                      className="flex items-center py-3 hover:bg-[#F0F5FF] -mx-5 px-5 transition-colors"
                    >
                      <span className="text-[17px] font-bold text-[#000A1E] w-10">{overdueBillsCount}</span>
                      <span className="flex-1 text-[13px] text-[#000A1E]">Overdue bills</span>
                      <ArrowRight className="w-4 h-4 text-[#A6A9B0]" />
                    </Link>
                  )}
                </>
              )}

              {unreconciledCount === 0 && overdueInvoicesCount === 0 && overdueBillsCount === 0 && awaitingApprovalCount === 0 && (
                <div className="flex items-center gap-2 py-4 text-[13px] text-[#00823C]">
                  <CheckCircle className="w-4 h-4" /> All caught up!
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ---- Document Inbox Card ---- */}
        <Card size="dashboard">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[17px] font-bold text-[#000A1E]">Document Inbox</h3>
              {inboxDocs.length > 0 && (
                <span className="bg-[#FFF3CD] text-[#856404] text-[11px] font-bold rounded-[3px] px-2 py-0.5">
                  {inboxDocs.length} to review
                </span>
              )}
            </div>

            <div className="flex gap-6 mb-4">
              <div>
                <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">{inboxDocs.length}</p>
                <p className="text-[11px] text-[rgba(0,10,30,0.75)]">Inbox</p>
              </div>
              <div>
                <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">{filedCount}</p>
                <p className="text-[11px] text-[rgba(0,10,30,0.75)]">Filed</p>
              </div>
            </div>

            {inboxDocs.length > 0 ? (
              <div className="space-y-0">
                {inboxDocs.slice(0, 5).map((doc) => (
                  <Link
                    key={doc.id}
                    to={`/clients/${clientId}/documents`}
                    className="flex items-center gap-2.5 py-2.5 border-b border-[#E8E9EB] hover:bg-[#F0F5FF] -mx-5 px-5 transition-colors"
                  >
                    {doc.mime_type.startsWith('image/') ? (
                      <Image className="w-4 h-4 text-[#0078C8] shrink-0" />
                    ) : doc.mime_type === 'application/pdf' ? (
                      <FileText className="w-4 h-4 text-[#CC0000] shrink-0" />
                    ) : (
                      <File className="w-4 h-4 text-[#A6A9B0] shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-[#000A1E] truncate">{doc.file_name}</p>
                      <p className="text-[11px] text-[rgba(0,10,30,0.75)]">
                        {new Date(doc.uploaded_at).toLocaleDateString('en', { day: 'numeric', month: 'short' })}
                        {doc.category && <> &middot; {doc.category}</>}
                      </p>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-[#A6A9B0] shrink-0" />
                  </Link>
                ))}
                {inboxDocs.length > 5 && (
                  <p className="text-[12px] text-[rgba(0,10,30,0.75)] pt-2">
                    + {inboxDocs.length - 5} more document{inboxDocs.length - 5 > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 py-4 text-[13px] text-[#00823C]">
                <CheckCircle className="w-4 h-4" /> Inbox is empty
              </div>
            )}

            <div className="mt-4">
              <Button variant="pill" onClick={() => navigate(`/clients/${clientId}/documents`)}>
                <FolderOpen className="w-3.5 h-3.5" /> View documents
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="hidden md:block" />
      </div>

      {/* Row 3: Recent Payments | Cash In/Out | Net Profit/Loss */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* ---- Recent Invoice Payments ---- */}
        <Card size="dashboard">
          <CardContent className="p-5">
            <h3 className="text-[17px] font-bold text-[#000A1E] mb-4">Recent invoice payments</h3>
            {isPremium && recentPayments.length > 0 ? (
              <>
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-[12px] font-bold text-[#000A1E] border-b border-[#CCCED2]">
                      <th className="text-left py-1.5">Invoice #</th>
                      <th className="text-left py-1.5">Contact</th>
                      <th className="text-right py-1.5">Date</th>
                      <th className="text-right py-1.5">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPayments.map((inv) => (
                      <tr key={inv.id} className="border-b border-[#E8E9EB]">
                        <td className="py-2 text-[#0078C8] font-bold">
                          <button onClick={() => navigate(`/clients/${clientId}/invoices/${inv.id}/edit`)} className="hover:underline">
                            {inv.invoice_number}
                          </button>
                        </td>
                        <td className="py-2">{inv.contact_name}</td>
                        <td className="py-2 text-right text-[rgba(0,10,30,0.75)]">
                          {new Date(inv.issue_date).toLocaleDateString('en', { day: 'numeric', month: 'short' })}
                        </td>
                        <td className="py-2 text-right tabular-nums">{fc(inv.amount_paid)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-4">
                  <Button variant="pill" onClick={() => navigate(`/clients/${clientId}/invoices`)}>
                    <Send className="w-3.5 h-3.5" /> Send invoices
                  </Button>
                </div>
              </>
            ) : isPremium ? (
              <p className="text-[13px] text-[rgba(0,10,30,0.75)] py-4">No payments recorded yet.</p>
            ) : (
              <p className="text-[13px] text-[rgba(0,10,30,0.75)] py-4">Upgrade to Premium for invoice tracking.</p>
            )}
          </CardContent>
        </Card>

        {/* ---- Cash In and Out ---- */}
        <Card size="dashboard">
          <CardContent className="p-5">
            <h3 className="text-[17px] font-bold text-[#000A1E] mb-3">Cash in and out</h3>
            <div className="flex gap-6 mb-1">
              <div>
                <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">{fc(cashIn)}</p>
                <p className="text-[11px] text-[rgba(0,10,30,0.75)]">Cash in</p>
              </div>
              <div>
                <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">{fc(cashOut)}</p>
                <p className="text-[11px] text-[rgba(0,10,30,0.75)]">Cash out</p>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-[17px] font-bold text-[#000A1E] tabular-nums">{fc(cashDiff)}</p>
              <p className="text-[11px] text-[rgba(0,10,30,0.75)]">Difference</p>
            </div>
            {/* Simple 2-bar visual */}
            <div className="flex items-end gap-6 justify-center" style={{ height: 120 }}>
              <div className="flex flex-col items-center">
                <div
                  className="w-16 rounded-t-sm bg-[#002A46]"
                  style={{ height: cashIn > 0 ? `${Math.max((cashIn / Math.max(cashIn, cashOut, 1)) * 120, 4)}px` : '0px' }}
                />
                <span className="text-[10px] text-[rgba(0,10,30,0.75)] mt-1">Cash in</span>
              </div>
              <div className="flex flex-col items-center">
                <div
                  className="w-16 rounded-t-sm bg-[#A6A9B0]"
                  style={{ height: cashOut > 0 ? `${Math.max((cashOut / Math.max(cashIn, cashOut, 1)) * 120, 4)}px` : '0px' }}
                />
                <span className="text-[10px] text-[rgba(0,10,30,0.75)] mt-1">Cash out</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ---- Net Profit or Loss ---- */}
        <Card size="dashboard">
          <CardContent className="p-5">
            <h3 className="text-[17px] font-bold text-[#000A1E] mb-3">Net profit or loss</h3>
            <p className="text-[21px] font-bold text-[#000A1E] tabular-nums">{fc(plNet)}</p>
            {profitLoss && (
              <p className="text-[11px] text-[rgba(0,10,30,0.75)] mb-4">
                {profitLoss.start_date} – {profitLoss.end_date}
              </p>
            )}
            {/* Income vs Expenses bar chart */}
            <div className="flex items-end gap-8 justify-center" style={{ height: 130 }}>
              <div className="flex flex-col items-center">
                <div
                  className="w-20 rounded-t-sm bg-[#002A46]"
                  style={{ height: `${Math.max((plRevenue / plMax) * 130, plRevenue > 0 ? 4 : 0)}px` }}
                />
                <span className="text-[10px] text-[rgba(0,10,30,0.75)] mt-1 text-center">
                  Income<br />{fc(plRevenue)}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <div
                  className="w-20 rounded-t-sm bg-[#A6A9B0]"
                  style={{ height: `${Math.max((plExpenses / plMax) * 130, plExpenses > 0 ? 4 : 0)}px` }}
                />
                <span className="text-[10px] text-[rgba(0,10,30,0.75)] mt-1 text-center">
                  Expenses<br />{fc(plExpenses)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
